/**
 * The host side of the tab ↔ page bridge, shared by every tab host (a tabbed
 * pop-out window, and the main window's own strip).
 *
 * Each tab is a same-origin iframe of a page pop-out. Pages talk to their host
 * over postMessage (lib/popout.ts FrameMessage). A message is accepted only at
 * our own origin, only from one of OUR iframes (matched by contentWindow, which
 * also tells us which tab spoke), and only in a shape parseFrameMessage knows.
 */
import { useEffect, useRef, type MutableRefObject } from 'react';
import { parseFrameMessage, type FrameMessage } from '@/lib/popout';

export type FrameMap = MutableRefObject<Map<string, HTMLIFrameElement>>;

/** Which tab's iframe a message came from, or null if none of ours. */
export function frameUidForSource(frames: Map<string, HTMLIFrameElement>, source: MessageEventSource | null): string | null {
  if (!source) return null;
  for (const [uid, frame] of frames) {
    if (frame.contentWindow && frame.contentWindow === source) return uid;
  }
  return null;
}

/**
 * Listen for page → host messages.
 * @param onOther same-origin messages that did not come from one of our frames
 *   (a tabbed window uses this for "add these tabs" from the window that opened it).
 */
export function useFrameBridge(
  frames: FrameMap,
  onFrameMessage: (uid: string, msg: FrameMessage) => void,
  onOther?: (e: MessageEvent) => void,
  enabled = true,
): void {
  const handlers = useRef({ onFrameMessage, onOther });
  handlers.current = { onFrameMessage, onOther };

  useEffect(() => {
    if (!enabled) return;
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const uid = frameUidForSource(frames.current, e.source);
      if (!uid) {
        handlers.current.onOther?.(e);
        return;
      }
      const msg = parseFrameMessage(e.data);
      if (msg) handlers.current.onFrameMessage(uid, msg);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [frames, enabled]);
}

/** Ref callback that keeps `frames` in step with the mounted iframes. */
export function frameRef(frames: FrameMap, uid: string) {
  return (el: HTMLIFrameElement | null) => {
    if (el) frames.current.set(uid, el);
    else frames.current.delete(uid);
  };
}
